/**
 * Portal — the gate to the next level.
 *
 * Starts dormant (dark arch). When all puzzle conditions are met,
 * it activates with a swirling particle vortex. Both cats must walk
 * through to complete the level.
 *
 * Emits PORTAL_OPENED and PORTAL_ENTERED events.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Material } from "@babylonjs/core/Materials/material";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { InteractiveObject, InteractiveConfig, ReadabilityState } from "./InteractiveObject";
import type { InstantiatedModel } from "../../systems/AssetManager";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { setMeshVisibility } from "../../core/meshVisibility";
import { createSoftParticleTexture } from "../../core/particleTextures";
import { registerHoldStation } from "../../systems/navigation/HoldStations";

export interface PortalConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  /**
   * Optional override for the both-cats entry-detection zone half-extents
   * (x = horizontal, y = vertical), decoupled from the visual arch size.
   * The default zone (half the visual size: ±0.75u × ±1.25u) is a tight
   * fit for two colliding cat capsules, and in solo mode the idle
   * follower parks ~1.5u away from the active cat — outside the default
   * zone. Levels can widen the zone without inflating the arch art.
   */
  entryHalfExtents?: { x: number; y: number };
}

export class Portal extends InteractiveObject {
  private isOpen = false;
  private archMesh: Mesh;
  private innerMesh: Mesh;
  private innerMat: StandardMaterial;
  private vortexParticles: ParticleSystem | null = null;
  private pulseAnimation: Animation | null = null;

  /**
   * PBR materials collected from the loaded crystal-vine door GLB.
   * `onModelLoaded` hides the procedural torus + disc, so open-state
   * visuals MUST also write to these or opening only reads as the
   * particle vortex on final art (Q-036b). Crystal/vine materials get
   * the bioluminescent-blue ignition; brass gets a purple accent.
   * Both stay empty when the GLB fails to load (procedural fallback).
   */
  private glbCrystalMats: PBRMaterial[] = [];
  private glbAccentMats: PBRMaterial[] = [];

  /** Detection zone half-extents for checking cat entry. */
  private readonly entryHalfWidth: number;
  private readonly entryHalfHeight: number;

  constructor(scene: Scene, config: PortalConfig) {
    const size = config.size ?? new Vector3(1.5, 2.5, 0.4);
    super(scene, { ...config, size });

    this.entryHalfWidth = config.entryHalfExtents?.x ?? size.x * 0.5;
    this.entryHalfHeight = config.entryHalfExtents?.y ?? size.y * 0.5;

    // Outer arch frame (torus or rounded box)
    this.archMesh = MeshBuilder.CreateTorus(
      `portal_${config.key}_arch`,
      {
        diameter: size.x * 1.1,
        thickness: 0.15,
        tessellation: 32,
      },
      scene
    );
    this.archMesh.parent = this.rootMesh;
    this.archMesh.position = new Vector3(0, size.y * 0.3, 0);
    this.archMesh.rotation.x = Math.PI / 2; // Stand the torus upright
    this.archMesh.scaling = new Vector3(1, size.y / size.x, 1);

    const archMat = new StandardMaterial(`portal_${config.key}_archMat`, scene);
    archMat.diffuseColor = new Color3(0.1, 0.07, 0.15); // Dark stone
    archMat.specularColor = Color3.Black();
    archMat.emissiveColor = Color3.Black();
    this.archMesh.material = archMat;

    // Inner fill (opaque plane that shows the portal energy)
    this.innerMesh = MeshBuilder.CreateDisc(
      `portal_${config.key}_inner`,
      { radius: size.x * 0.45, tessellation: 32 },
      scene
    );
    this.innerMesh.parent = this.rootMesh;
    this.innerMesh.position = new Vector3(0, size.y * 0.3, 0);

    this.innerMat = new StandardMaterial(`portal_${config.key}_innerMat`, scene);
    this.innerMat.diffuseColor = new Color3(0.05, 0.03, 0.1);
    this.innerMat.emissiveColor = Color3.Black();
    this.innerMat.alpha = 0.4;
    this.innerMat.specularColor = Color3.Black();
    this.innerMesh.material = this.innerMat;

    // Use arch material for base state management
    this.material = archMat;

    // Attempt to load the ornate crystal-vine door GLB authored for
    // Level 0. Falls back to the procedural torus arch + disc above if
    // the GLB fails to load. `onModelLoaded` hides the procedural mesh
    // so the GLB doesn't visually overlap it.
    this.loadModelOverride("/assets/models/environment/level0/crystal_vine_portal_door.glb");

    // Solo-mode hold station: a cat parked inside an OPEN portal's entry
    // zone is waiting for its partner — the companion follow must not
    // walk it back out while the player fetches the other cat.
    this.unregisterHoldStation = registerHoldStation(scene, {
      key: `portal_${config.key}`,
      contains: (pos) =>
        this.isOpen && this.isInsideZone(pos, this.rootMesh.getAbsolutePosition()),
    });
  }

  private unregisterHoldStation: (() => void) | null = null;

  protected override onModelLoaded(instance: InstantiatedModel): void {
    // The GLB replaces the procedural torus + disc. Hide them so the
    // ornate door is the only thing the player sees, but keep them in
    // the mesh graph so the readability state code that targets them
    // still has a valid target.
    this.archMesh.isVisible = false;
    this.innerMesh.isVisible = false;

    // Collect the door's PBR materials so open()/completeLevel() can
    // drive emissive state on the visible art (CrystalReceiver pattern).
    // Material names verified in crystal_vine_portal_door.glb:
    // DoorDarkwood, DoorCrystal, DoorBrass, DoorVine. Dedupe by ref —
    // several meshes share each material.
    const seen = new Set<Material>();
    for (const mesh of instance.meshes) {
      const mat = mesh.material;
      if (!mat || seen.has(mat)) continue;
      seen.add(mat);
      if (!(mat instanceof PBRMaterial)) continue;
      if (mat.name.includes("DoorCrystal") || mat.name.includes("DoorVine")) {
        this.glbCrystalMats.push(mat);
      } else if (mat.name.includes("DoorBrass")) {
        this.glbAccentMats.push(mat);
      }
    }

    // If the GLB arrived after open() already fired (async load race),
    // re-apply the open-state emissive so the door doesn't stay dark.
    if (this.isOpen) {
      this.applyOpenGlbEmissive();
    }
  }

  /**
   * Open-state emissive on the GLB door materials. Plain uniform writes
   * only — no transparencyMode flips, no new meshes/materials — so the
   * open()-frame latency fix (deferred VFX) is preserved.
   */
  private applyOpenGlbEmissive(): void {
    const crystalGlow = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE).scale(0.6);
    for (const m of this.glbCrystalMats) {
      m.emissiveColor = crystalGlow;
      m.emissiveIntensity = 1.6;
    }
    const accentGlow = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE).scale(0.25);
    for (const m of this.glbAccentMats) {
      m.emissiveColor = accentGlow;
    }
  }

  /**
   * Open the portal (all puzzle prerequisites met).
   */
  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.state = "active";

    // Lightweight material flips happen inline so the portal reads as
    // "open" this frame.
    this.innerMat.diffuseColor = Color3.FromHexString(COLORS.DEEP_PURPLE);
    this.innerMat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE).scale(0.5);
    this.innerMat.alpha = 0.8;

    const archMat = this.archMesh.material as StandardMaterial;
    archMat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE).scale(0.3);

    // Drive the visible GLB door too — the procedural meshes above are
    // hidden whenever the door art loaded, so without this the "open"
    // state only read as 200 particle specks (Q-036b). Uniform writes
    // only; heavy VFX stays behind the deferred block below.
    this.applyOpenGlbEmissive();

    gameEvents.emit(GameEvents.PORTAL_OPENED, this.key);

    // Defer the heavy VFX setup (particle system + animation) to the
    // next render tick. Portal.open() fires in the same frame as
    // crystal.solveCrystal() + dial.solve(), and the combined
    // synchronous overhead was causing a ~277ms input-latency spike on
    // the very next keypress. If preWarm() ran earlier, the shader is
    // already compiled and this deferred block is cheap.
    this.scene.onAfterRenderObservable.addOnce(() => {
      this.startVortexParticles();
      this.startPulseAnimation();
    });
  }

  /**
   * Pre-compile the portal VFX shader during scene init so `open()`
   * doesn't trigger a 300ms+ shader-compile hitch on the very frame a
   * player expects the portal to respond. Gemini 3.1-pro fluidity
   * review caught this as a 362ms input-latency spike at the portal-
   * ignite moment. Call after scene setup but before gameplay begins;
   * idempotent.
   */
  preWarmVfx(): void {
    if (this.vortexParticles) return;
    this.startVortexParticles();
    const ps = this.vortexParticles as ParticleSystem | null;
    // The shader compile happens the first time the system actually
    // RENDERS emitted particles, not when it's created. Letting the
    // system run for a couple of frames forces the compile; stopping
    // immediately (as the prior version did) skipped the compile and
    // the hitch just moved to the real open() frame. Gemini 3.1-pro
    // still flagged a 350ms spike post-portal-open on the L0 review
    // after the prior fix — this 2-frame warmup actually resolves it.
    let framesWaited = 0;
    const warmupObserver = this.scene.onAfterRenderObservable.add(() => {
      framesWaited += 1;
      if (framesWaited >= 2) {
        ps?.stop();
        ps?.reset();
        this.scene.onAfterRenderObservable.remove(warmupObserver);
      }
    });
  }

  /**
   * Check if both cats are inside the portal zone.
   * @param minnaPos World position of Minna.
   * @param basilPos World position of Basil.
   * @returns true if level complete was triggered.
   */
  checkEntry(minnaPos: Vector3, basilPos: Vector3): boolean {
    if (!this.isOpen) return false;

    const portalPos = this.rootMesh.getAbsolutePosition();

    const minnaInside = this.isInsideZone(minnaPos, portalPos);
    const basilInside = this.isInsideZone(basilPos, portalPos);

    if (minnaInside && basilInside) {
      this.completeLevel();
      return true;
    }

    return false;
  }

  get isPortalOpen(): boolean {
    return this.isOpen;
  }

  override getHighlightTarget(): AbstractMesh {
    return this.archMesh;
  }

  override setReadabilityState(state: ReadabilityState): void {
    super.setReadabilityState(state);

    const setVisibility = (value: number) => {
      for (const mesh of this.rootMesh.getChildMeshes()) {
        setMeshVisibility(mesh, value);
      }
    };

    if (this.isOpen || this.state === "solved") {
      setVisibility(1);
      this.archMesh.visibility = 1;
      this.innerMesh.visibility = 1;
      return;
    }

    switch (state) {
      case "suppressed":
        setVisibility(0.25);
        this.archMesh.visibility = 0.25;
        this.innerMesh.visibility = 0.12;
        this.innerMat.alpha = 0.01;
        this.innerMat.emissiveColor = Color3.Black();
        break;
      case "hint":
        setVisibility(0.18);
        this.archMesh.visibility = 0.18;
        this.innerMesh.visibility = 0.1;
        this.innerMat.alpha = 0.04;
        this.innerMat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE).scale(0.05);
        break;
      case "focus":
      case "active":
        setVisibility(0.72);
        this.archMesh.visibility = 0.72;
        this.innerMesh.visibility = 0.35;
        this.innerMat.alpha = 0.16;
        this.innerMat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_PURPLE).scale(0.16);
        break;
      case "solved":
        setVisibility(1);
        this.archMesh.visibility = 1;
        this.innerMesh.visibility = 1;
        break;
      case "idle":
      default:
        setVisibility(0.28);
        this.archMesh.visibility = 0.28;
        this.innerMesh.visibility = 0.16;
        this.innerMat.alpha = 0.06;
        this.innerMat.emissiveColor = Color3.Black();
        break;
    }
  }

  dispose(): void {
    this.unregisterHoldStation?.();
    this.unregisterHoldStation = null;
    this.vortexParticles?.dispose();
    super.dispose();
  }

  // ─── Private ─────────────────────────────────────────────────────────

  private isInsideZone(pos: Vector3, portalPos: Vector3): boolean {
    const dx = Math.abs(pos.x - portalPos.x);
    const dy = Math.abs(pos.y - portalPos.y);
    return dx < this.entryHalfWidth && dy < this.entryHalfHeight;
  }

  private completeLevel(): void {
    this.state = "solved";

    // Flash white effect on inner mesh
    this.innerMat.emissiveColor = new Color3(1, 1, 1);

    // Mirror the entry flash on the GLB door crystals so portal ENTRY
    // also reads on final art (the procedural innerMesh is hidden when
    // the door GLB loaded). White pulse now; the innerMesh flash anim
    // below carries the timing and its onEnd callback restores the
    // settled blue. Plain uniform writes — no new materials/meshes.
    for (const m of this.glbCrystalMats) {
      m.emissiveColor = Color3.White();
      m.emissiveIntensity = 2.0;
    }

    // Fade back after a moment, then emit level complete
    const fps = 30;
    const flashAnim = new Animation(
      `portalFlash_${this.key}`,
      "material.emissiveColor",
      fps,
      Animation.ANIMATIONTYPE_COLOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    flashAnim.setKeys([
      { frame: 0, value: new Color3(1, 1, 1) },
      { frame: 15, value: Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE) },
      { frame: 36, value: Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE).scale(0.5) },
    ]);
    this.innerMesh.animations = [flashAnim];
    this.scene.beginAnimation(this.innerMesh, 0, 36, false, 1, () => {
      // Settle the GLB door crystals back to the open-state blue after
      // the white entry flash.
      const settled = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE).scale(0.6);
      for (const m of this.glbCrystalMats) {
        m.emissiveColor = settled;
        m.emissiveIntensity = 1.6;
      }
      gameEvents.emit(GameEvents.PORTAL_ENTERED, this.key);
    });
  }

  private startVortexParticles(): void {
    // Reuse the pre-warmed system if preWarmVfx() created it — avoids
    // re-compiling the shader at the moment of reveal.
    if (this.vortexParticles) {
      this.vortexParticles.start();
      return;
    }
    this.vortexParticles = new ParticleSystem(
      `portal_${this.key}_particles`,
      450,
      this.scene
    );

    this.vortexParticles.particleTexture = createSoftParticleTexture(
      this.scene,
      `portal_${this.key}_particleTexture`,
    );

    // Emitter at the portal center
    const portalPos = this.rootMesh.position;
    this.vortexParticles.emitter = new Vector3(
      portalPos.x,
      portalPos.y + this.config.size.y * 0.3,
      portalPos.z
    );

    // Particle settings for a swirl effect
    this.vortexParticles.minSize = 0.08;
    this.vortexParticles.maxSize = 0.22;
    this.vortexParticles.minLifeTime = 0.8;
    this.vortexParticles.maxLifeTime = 1.6;
    this.vortexParticles.emitRate = 140;
    this.vortexParticles.minEmitPower = 0.8;
    this.vortexParticles.maxEmitPower = 2.0;

    // Emit in a ring pattern
    this.vortexParticles.createSphereEmitter(this.config.size.x * 0.4);

    // Color gradient: purple -> blue -> green fade-out
    this.vortexParticles.color1 = new Color4(0.4, 0.0, 1.0, 1.0);
    this.vortexParticles.color2 = new Color4(0.0, 0.8, 1.0, 1.0);
    this.vortexParticles.colorDead = new Color4(0.0, 1.0, 0.5, 0.0);

    this.vortexParticles.blendMode = ParticleSystem.BLENDMODE_ADD;

    this.vortexParticles.start();
  }

  private startPulseAnimation(): void {
    const fps = 30;
    this.pulseAnimation = new Animation(
      `portalPulse_${this.key}`,
      "material.alpha",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );
    this.pulseAnimation.setKeys([
      { frame: 0, value: 0.6 },
      { frame: 12, value: 0.9 },
      { frame: 24, value: 0.6 },
    ]);

    this.innerMesh.animations = [this.pulseAnimation];
    this.scene.beginAnimation(this.innerMesh, 0, 24, true);
  }
}
